import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import {
  AppBar,
  Modal,
  SxProps,
  useScrollTrigger,
  useTheme
} from "@mui/material";
import { IDarkTheme } from "styles/darkTheme";
import { useNavigate } from "react-router-dom";
import DemoRequestForm from "../demoRequestForm";
import { Theme } from "@emotion/react";
import { useCurrentUser } from "app/appHooks";
import { PATH_NAME } from "routes/pathNames";
import styles from "./style";

const drawerWidth = 240;
export type Items = {
  title: string;
  onItemClick: any;
};
type IAppBarProps = {
  isSM: boolean;
  navItems: Items[];
  setAlwaysTriggered?: boolean;
};

export default function DrawerAppBar({
  navItems,
  isSM,
  setAlwaysTriggered
}: IAppBarProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openDemoRequestModal, setOpenDemoRequestModal] = React.useState(false);
  const { data: user } = useCurrentUser();
  const theme = useTheme<IDarkTheme>();
  const classes = styles();

  const trigger =
    useScrollTrigger({
      disableHysteresis: true,
      threshold: 1
    }) || setAlwaysTriggered;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const history = useNavigate();
  const onGetStarted = () => {
    history(user ? PATH_NAME.HOME_PAGE : PATH_NAME.LOGIN);
  };

  const viewDemoRequestModal = () => {
    setOpenDemoRequestModal(true);
  };
  const closeDemoRequestModal = () => {
    setOpenDemoRequestModal(false);
  };

  const RequestDemoButton = ({ sx }: { sx?: SxProps<Theme> | undefined }) => (
    <Button
      onClick={viewDemoRequestModal}
      variant="outlined"
      sx={{
        color: {
          xs: theme.colors.secondary.secondary_50,
          lg: setAlwaysTriggered
            ? theme.colors.secondary.secondary_50
            : trigger
            ? theme.colors.secondary.secondary_50
            : theme.colors.semantic.semantic_white
        },
        borderColor: {
          xs: theme.colors.secondary.secondary_50,
          lg: trigger
            ? theme.colors.secondary.secondary_50
            : theme.colors.semantic.semantic_white
        },
        borderRadius: "10px",
        fontFamily: theme.fontFamily.montserrat,
        fontWeight: theme.fontWeight.bold,
        fontSize: isSM ? theme.fontSize.bodyText_3 : theme.fontSize.bodyText_1,
        textTransform: "none",
        padding: isSM ? "5px" : undefined,
        ...sx
      }}
    >
      Get a Demo
    </Button>
  );
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.title} disablePadding>
            <ListItemButton
              sx={{ textAlign: "center" }}
              onClick={item.onItemClick}
            >
              <ListItemText primary={item.title} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton
            sx={{ textAlign: "center" }}
            onClick={viewDemoRequestModal}
          >
            <RequestDemoButton
              sx={{ marginLeft: "auto", marginRight: "auto" }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        position: { lg: "fixed" },
        width: "100%",
        zIndex: "2000"
      }}
    >
      <h1 style={{ display: "none" }}>Hoya App</h1>
      <AppBar
        sx={{
          backgroundColor: trigger ? "white" : "transparent",
          border: "none",
          boxShadow: isSM ? "unset" : trigger ? undefined : "unset"
        }}
        component={"nav"}
      >
        <Toolbar sx={{ width: "100%", display: "flex", alignItems: "center" }}>
          <IconButton
            color="primary"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { lg: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <img
            className={classes.logo}
            onClick={() => history(PATH_NAME.LANDING_PAGE)}
            alt="The house from the offer."
            src={`${process.env.PUBLIC_URL}/main-logo.svg`}
            width={170}
            height={51}
            {...{ fetchpriority: "high" }}
          />
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              marginLeft: "auto",
              marginRight: "88px"
            }}
          >
            {navItems.map((item) => (
              <Button
                key={item.title}
                sx={{
                  color: trigger
                    ? theme.colors.secondary.secondary_50
                    : theme.colors.semantic.semantic_white,
                  textTransform: "none",
                  fontFamily: theme.fontFamily.montserrat,
                  fontWeight: theme.fontWeight.big,
                  fontSize: theme.fontSize.bodyText_1,
                  marginLeft: "32px"
                }}
                onClick={item.onItemClick}
              >
                {item.title}
              </Button>
            ))}
          </Box>
          {!user && (
            <RequestDemoButton
              sx={{
                marginLeft: { xs: "auto", lg: 0 },
                display: { xs: "none", lg: "block" }
              }}
            />
          )}
          <Button
            onClick={onGetStarted}
            variant="outlined"
            sx={{
              color: {
                xs: theme.colors.secondary.secondary_50,
                lg: setAlwaysTriggered
                  ? theme.colors.secondary.secondary_50
                  : trigger
                  ? theme.colors.secondary.secondary_50
                  : theme.colors.semantic.semantic_white
              },
              borderColor: {
                xs: theme.colors.secondary.secondary_50,
                lg: trigger
                  ? theme.colors.secondary.secondary_50
                  : theme.colors.semantic.semantic_white
              },
              marginLeft: { xs: "auto", lg: "0.5rem" },
              borderRadius: "10px",
              fontFamily: theme.fontFamily.montserrat,
              fontWeight: theme.fontWeight.bold,
              fontSize: isSM
                ? theme.fontSize.bodyText_3
                : theme.fontSize.bodyText_1,
              textTransform: "none",
              padding: isSM ? "5px" : undefined
            }}
          >
            {user ? "View Dashboard" : "Get Started"}
          </Button>
        </Toolbar>
      </AppBar>
      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth
            }
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box component="main">
        <Toolbar />
      </Box>
      <Modal
        open={openDemoRequestModal}
        onClose={closeDemoRequestModal}
        aria-labelledby="Request a Demo"
        aria-describedby="Request a Demo"
      >
        <Box>
          <DemoRequestForm handleClose={closeDemoRequestModal} />
        </Box>
      </Modal>
    </Box>
  );
}
